/* eslint-disable react-hooks/exhaustive-deps */
import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FaBookmark, FaShapes } from 'react-icons/fa';
import { GiAmericanFootballHelmet } from 'react-icons/gi';
import { LuAppWindow, LuImage, LuType } from 'react-icons/lu';
import { PiBoundingBox } from 'react-icons/pi';
import { useAPIClient } from '../../../context/apiClient';
import {
  useComponentImportDispatch,
  useComponentImportState,
} from '../../../context/componentImport';
import {
  AdElement,
  AdElementImage,
  AdElementTeamAsset,
  AdElementCasinoAsset,
  Datasources,
  isImageElement,
  isSVGElement,
  isTeamAssetElement,
  isTextElement,
  isVectorElement,
  isCasinoAssetElement,
  isWidgetElement,
  parseElementName,
  buildCustomAssetContentUrl,
  hasInlineTokens,
} from '@adflow/types';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Icon,
  Image,
  ModalBody,
  ModalFooter,
  Skeleton,
  Stack,
  Text,
  useBoolean,
} from '@chakra-ui/react';
import { EditorStoreContext } from '@adflow/editor';
import React from 'react';
import { StepProps } from '..';
import ImgElTypeRadioGroup, { ImgElType } from './ImgElTypeRadioGroup';
import DataMappingMagic from './DataMappingMagic';
import { elementSelectionHelper } from './elementSelection.helpers';
import { UserContext } from '@adflow/utils';
import { CasinoIcon } from '@adflow/editor/src/icons/casino';

export type StepThreeSubmit = (
  customElements: Array<AdElement>,
  thumbnailURL: string,
  dataSourceTypes?: Array<Datasources.SourceType>,
) => void;

interface StepThreeProps extends StepProps {
  onSubmit: StepThreeSubmit;
}

const ComponentImportStepThree: FC<PropsWithChildren<StepThreeProps>> = ({ onClose, onSubmit }) => {
  const ctx = useContext(EditorStoreContext);
  const state = useComponentImportState();
  const [teamAssetElIds, setTeamAssetElIds] = useState<Array<string>>([]);
  const [casinoAssetElIds, setCasinoAssetElIds] = useState<Array<string>>([]);
  const [isSaved, setIsSaved] = useState(false);
  const dispatch = useComponentImportDispatch();
  const client = useAPIClient();

  const [casinoTemplatesEnabled, setCasinoTemplatesEnabled] = useState(false);
  // A saved component can hold widgets added by someone with the feature flag. Without it the
  // template could not be saved with them, so they are left out, as before widgets existed.
  const [widgetLayersEnabled, setWidgetLayersEnabled] = useState(false);
  const [importOverrides, setImportOverrides] = useState<Datasources.ImportDataSourceOverrides>({});
  const { user } = useContext(UserContext);

  const isRunningInsideCypress =
    typeof window !== 'undefined' && Boolean((window as unknown as { Cypress?: boolean }).Cypress);

  useEffect(() => {
    // Each flag is read on its own, so a failed lookup only turns off its own feature.
    const readFeature = async (feature: string) => {
      try {
        return (await user?.hasFeature(feature)) ?? false;
      } catch (error) {
        console.warn(`Failed to check feature ${feature}`, error);
        return false;
      }
    };
    readFeature('cm-casino-templates').then((enabled) =>
      setCasinoTemplatesEnabled(enabled || isRunningInsideCypress),
    );
    readFeature('cm-widget-layers').then(setWidgetLayersEnabled);
  }, [user]);

  const toggleSaveComponent = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsSaved(e.target.checked);
  }, []);

  const suggestedDataSources = useMemo(
    () =>
      Datasources.suggestDataSources([
        ...state.elements.filter(isTextElement),
        ...state.elements.filter(isImageElement),
      ]),
    [state.elements],
  );

  const inferredImportConfig = useMemo(
    () => Datasources.inferImportDataSourceConfig(suggestedDataSources),
    [suggestedDataSources],
  );

  const sourceFamilyOptions = useMemo(
    () =>
      inferredImportConfig
        ? []
        : Datasources.getImportDataSourceFamilyOptions(suggestedDataSources),
    [suggestedDataSources, inferredImportConfig],
  );

  const selectedSourceFamily =
    importOverrides.sourceFamily ??
    (!inferredImportConfig && sourceFamilyOptions.length === 1
      ? sourceFamilyOptions[0].value
      : null);

  const competitionOptions = useMemo(
    () =>
      inferredImportConfig?.sourceFamily === 'SPORTS_MOMENTS' ||
      selectedSourceFamily === 'SPORTS_MOMENTS'
        ? Datasources.getImportDataSourceCompetitionOptions()
        : [],
    [inferredImportConfig, selectedSourceFamily],
  );

  const resolvedImportConfig = useMemo(
    () =>
      Datasources.resolveImportDataSourceConfig(
        inferredImportConfig,
        { ...importOverrides, sourceFamily: selectedSourceFamily },
        suggestedDataSources,
      ),
    [suggestedDataSources, inferredImportConfig, importOverrides, selectedSourceFamily],
  );

  const selectedCompetition =
    resolvedImportConfig?.sourceFamily === 'SPORTS_MOMENTS' ||
    selectedSourceFamily === 'SPORTS_MOMENTS'
      ? importOverrides.competition ??
        (resolvedImportConfig?.sourceFamily === 'SPORTS_MOMENTS'
          ? resolvedImportConfig.competition
          : competitionOptions.length === 1
          ? competitionOptions[0].value
          : null)
      : null;

  const selectedStage = useMemo(() => {
    if (!resolvedImportConfig) {
      return null;
    }

    const stageOptions = Datasources.getImportDataSourceStageOptions(resolvedImportConfig);
    if (
      importOverrides.stage != null &&
      stageOptions.some((option) => option.value === importOverrides.stage)
    ) {
      return importOverrides.stage;
    }

    return resolvedImportConfig.sourceFamily === 'SPORTS_MOMENTS' ||
      resolvedImportConfig.sourceFamily === 'CAR_RACING'
      ? resolvedImportConfig.stage
      : null;
  }, [resolvedImportConfig, importOverrides.stage]);

  const filteredSuggestions = useMemo(() => {
    return Datasources.filterImportDataSourceSuggestions(
      suggestedDataSources,
      resolvedImportConfig,
      importOverrides,
    );
  }, [suggestedDataSources, resolvedImportConfig, importOverrides]);

  const dataSourceTypes = useMemo(
    () =>
      Datasources.buildImportDataSourceTypes(
        resolvedImportConfig,
        importOverrides,
        filteredSuggestions,
      ),
    [filteredSuggestions, resolvedImportConfig, importOverrides],
  );

  /** Restrict layer/token resolution to types in this import (same idea as {@link Datasources.pickSuggestionGroupForElementImport}). */
  const importScopedDataSources = useMemo(() => {
    if (dataSourceTypes.length === 0) {
      return ctx.dataSources;
    }
    const allowed = new Set(dataSourceTypes);
    const typeOrder = new Map(dataSourceTypes.map((t, i) => [t, i]));
    return [...ctx.dataSources]
      .filter((ds) => allowed.has(ds.type))
      .sort((a, b) => (typeOrder.get(a.type) ?? 0) - (typeOrder.get(b.type) ?? 0));
  }, [ctx.dataSources, dataSourceTypes]);

  const isImplicitDataPointName = useCallback(
    (el: AdElement): boolean =>
      isTextElement(el) &&
      Datasources.resolveLayerNameToDataPoint(el.name, importScopedDataSources) !== null,
    [importScopedDataSources],
  );

  const hasImplicitDataReferences = useMemo(
    () =>
      state.elements.some(
        (el) =>
          (isTextElement(el) && hasInlineTokens(el.data?.content)) || isImplicitDataPointName(el),
      ),
    [state.elements, isImplicitDataPointName],
  );

  const [mappingEnabled, { on: enableDataMapping, toggle: toggleDataMapping }] = useBoolean(
    filteredSuggestions.length > 0 || hasImplicitDataReferences,
  );

  const prevFilteredSuggestionCountRef = useRef(0);
  useEffect(() => {
    const count = filteredSuggestions.length;
    const prev = prevFilteredSuggestionCountRef.current;
    if (prev === 0 && count > 0) {
      enableDataMapping();
    }
    prevFilteredSuggestionCountRef.current = count;
  }, [filteredSuggestions, enableDataMapping]);

  const [checkedElements, setCheckedElements] = useState(
    [...state.elements]
      .map((el) => {
        return { selected: elementSelectionHelper.isAlwaysImported(el), ...el };
      })
      .sort((a, b) => {
        if (a.selected && !b.selected) return -1;
        if (!a.selected && b.selected) return 1;
        return a.type.localeCompare(b.type) || a.name.localeCompare(b.name);
      })
      .map((el) => ({
        ...el,
        selected:
          filteredSuggestions.some((dps) => dps.some((dp) => dp.elId === el.id)) ||
          elementSelectionHelper.isAlwaysImported(el) ||
          (isTextElement(el) && hasInlineTokens(el.data?.content)) ||
          isImplicitDataPointName(el),
      })),
  );

  useEffect(() => {
    setCheckedElements((prev) =>
      prev.map((el) => ({
        ...el,
        selected:
          el.selected ||
          filteredSuggestions.some((dps) => dps.some((dp) => dp.elId === el.id)) ||
          (isTextElement(el) && hasInlineTokens(el.data?.content)) ||
          isImplicitDataPointName(el),
      })),
    );
  }, [filteredSuggestions, isImplicitDataPointName]);

  const lookupDataSource = useCallback(
    (elId: string) => {
      const matchingGroups = filteredSuggestions.filter((dps) =>
        dps.some((dp) => dp.elId === elId),
      );

      const chosenGroup = Datasources.pickSuggestionGroupForElementImport(
        matchingGroups,
        elId,
        dataSourceTypes,
      );

      const dataPoints = chosenGroup?.filter((dp) => dp.elId === elId);

      if (!dataPoints) {
        return {};
      }
      const dataPoint = dataPoints[0];
      const dataSource = ctx.dataSources.find((ds) => ds.type === dataPoint.type);

      if (!dataSource) {
        return {};
      }
      return { dataSource, dataPoint };
    },
    [filteredSuggestions, ctx.dataSources, dataSourceTypes],
  );

  const mapCustomImageElement = useCallback(
    (el: AdElementImage): AdElementImage => {
      const parsed = parseElementName(el.name);
      if (!mappingEnabled || !parsed.config?.customAssetConfig) return el;

      const { dataSource, dataPoint } = lookupDataSource(el.id);
      if (!dataSource || !dataPoint) return el;

      const contentUrl = buildCustomAssetContentUrl(
        parsed.config.customAssetConfig,
        dataPoint.dataPointName,
        dataSource.id,
      );

      return {
        ...el,
        data: {
          ...el.data,
          content: contentUrl,
        },
        sources: [
          ...el.sources,
          {
            sourceId: dataSource.id,
            name: dataPoint.dataPointName as Datasources.AllowableDataPoints,
          },
        ],
      };
    },
    [mappingEnabled, lookupDataSource],
  );

  const handleSubmit: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    async (evt) => {
      evt.preventDefault();
      const svgElements = state.elements.filter(isSVGElement);
      const textElements = state.elements.filter(isTextElement);
      const imageElements = state.elements.filter(isImageElement);
      const teamAssetElements = state.elements.filter(isTeamAssetElement);
      const casinoAssetElements = state.elements.filter(isCasinoAssetElement);
      const vectorElements = state.elements.filter(isVectorElement);
      // Left out without the feature flag, so the stored widgets of the component stay as they are.
      const widgetElements = widgetLayersEnabled
        ? state.elements.filter(isWidgetElement)
        : undefined;

      const extraSourceTypes = new Set<Datasources.SourceType>();

      const filteredElements = checkedElements
        .filter((el) => el.selected && (el.type !== 'WIDGET' || widgetLayersEnabled))
        .map((el) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { selected, ...rest } = el;

          if (isTextElement(rest)) {
            const resolved = Datasources.resolveTextElementTokens(rest, importScopedDataSources);
            if (resolved) {
              if (!mappingEnabled) {
                resolved.sourceTypes.forEach((t) => extraSourceTypes.add(t));
              }
              return resolved.element;
            }
            if (!mappingEnabled) return rest;
            const { dataSource, dataPoint } = lookupDataSource(rest.id);
            if (!dataSource || !dataPoint) return rest;
            return {
              ...rest,
              data: {
                ...rest.data,
                content: `{${dataSource.id}:${dataPoint.dataPointName}}`,
              },
              sources: [
                ...rest.sources,
                {
                  sourceId: dataSource.id,
                  name: dataPoint.dataPointName as Datasources.AllowableDataPoints,
                },
              ],
            };
          } else if (isImageElement(rest)) {
            const parsed = parseElementName(rest.name);
            const isTeamAsset = teamAssetElIds.includes(rest.id);
            const isCasinoAsset = casinoAssetElIds.includes(rest.id);
            if (isTeamAsset) {
              const overrides = mappingEnabled ? lookupDataSource(rest.id) : {};
              return {
                ...rest,
                type: 'TEAM_ASSET',
                data: {
                  ...rest.data,
                  ...('dataPoint' in overrides
                    ? {
                        content: '',
                        source: elementSelectionHelper.mapDataPointToTeamAssetSource(
                          overrides.dataPoint?.dataPointName ?? '',
                        ),
                      }
                    : {}),
                },
              } as AdElementTeamAsset;
            }
            if (isCasinoAsset) {
              const overrides = mappingEnabled ? lookupDataSource(rest.id) : {};
              return {
                ...rest,
                position: { ...rest.position, z: -1 },
                type: 'CASINO_ASSET',
                data: {
                  ...rest.data,
                  ...('dataPoint' in overrides
                    ? {
                        content: '',
                        source: elementSelectionHelper.mapDataPointToCasinoAssetSource(
                          overrides.dataPoint?.dataPointName ?? '',
                        ),
                      }
                    : {}),
                },
              } as AdElementCasinoAsset;
            }
            if (parsed.config?.customAssetConfig && mappingEnabled) {
              return mapCustomImageElement(rest);
            }
            return rest;
          } else if (isVectorElement(rest)) {
            return elementSelectionHelper.mapVectorElement(rest, svgElements[0].data.svg);
          } else {
            return rest;
          }
        });

      if (mappingEnabled) {
        const assetType = filteredElements
          .filter(isTeamAssetElement)
          .map((el) => {
            const { dataPoint } = lookupDataSource(el.id);
            return dataPoint
              ? elementSelectionHelper.mapDataPointToDisplayAssetType(dataPoint.dataPointName)
              : undefined;
          })
          .filter((x) => x)
          .pop();
        if (assetType) {
          ctx.store
            ?.getState()
            .setDisplayOptions(
              'teamAssets',
              { type: assetType, fallback: 'NONE' },
              { skipHistory: true },
            );
        }
      }

      const mergedDataSourceTypes = mappingEnabled
        ? Array.from(new Set(dataSourceTypes))
        : Array.from(extraSourceTypes);
      onSubmit(filteredElements, state.componentThumbnailURL, mergedDataSourceTypes);
      if (!isSaved) {
        return;
      }

      if (!svgElements.length) {
        console.warn('expected at least one SVG element but received 0 ');
        return;
      }
      if (!textElements.length) {
        console.warn('expected at least one text element but received 0 ');
        return;
      }
      if (!imageElements.length) {
        console.warn('expected at least one text element but received 0 ');
        return;
      }
      const svgElement = svgElements[0];
      const svgString = svgElement.data.svg;
      try {
        await client.upsertFigmaComponent({
          figmaId: svgElement.id,
          fileId: state.fileId,
          name: svgElement.name,
          svg: svgString,
          svgUrl: state.previewURL,
          thumbnail: state.componentThumbnailURL,
          svgElement,
          textElements,
          imageElements,
          teamAssetElements,
          casinoAssetElements,
          vectorElements,
          widgetElements,
        });
      } catch {
        dispatch({
          type: 'error',
          error: 'An unexpected error occurred',
        });
      }
    },
    [
      state.elements,
      state.componentThumbnailURL,
      state.fileId,
      state.previewURL,
      checkedElements,
      onSubmit,
      dataSourceTypes,
      importScopedDataSources,
      isSaved,
      teamAssetElIds,
      casinoAssetElIds,
      widgetLayersEnabled,
      mappingEnabled,
      lookupDataSource,
      ctx.store,
      client,
      dispatch,
    ],
  );

  const onChangeHandle = useCallback(
    (inputIndex: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setCheckedElements(
        checkedElements.map((el, idx) => {
          if (inputIndex === idx) {
            return { ...el, selected: e.target.checked };
          }
          return el;
        }),
      );
    },
    [checkedElements],
  );

  useEffect(() => {
    if (!mappingEnabled) {
      return;
    }
    const elIds = new Set<string>();
    filteredSuggestions.forEach((dps) => {
      dps.forEach((dp) => {
        if (dp.elId) elIds.add(dp.elId);
      });
    });
    for (const elId of elIds) {
      const group = Datasources.pickSuggestionGroupForElementImport(
        filteredSuggestions,
        elId,
        dataSourceTypes,
      );
      if (!group) continue;
      group.forEach((dp) => {
        if (dp.type === 'CASINO' && casinoTemplatesEnabled) {
          setCasinoAssetElIds((prev) => [...prev.filter((id) => id !== dp.elId), dp.elId]);
        }
        if (elementSelectionHelper.isTeamAssetDataPoint(dp.dataPointName)) {
          setTeamAssetElIds((prev) => [...prev.filter((id) => id !== dp.elId), dp.elId]);
        }
      });
    }
  }, [filteredSuggestions, mappingEnabled, casinoTemplatesEnabled, dataSourceTypes]);

  const handleToggleImgElType = (elId: string, newType: ImgElType) => {
    const elIsTeamAssetEl = teamAssetElIds.includes(elId);
    const elIsCasinoEl = casinoAssetElIds.includes(elId);

    if (newType === 'teamasset') {
      if (!elIsTeamAssetEl) {
        setTeamAssetElIds((prev) => [...prev, elId]);
      }
      if (elIsCasinoEl) {
        setCasinoAssetElIds((prev) => prev.filter((id) => id !== elId));
      }

      return;
    }

    if (newType === 'img') {
      if (elIsTeamAssetEl) {
        setTeamAssetElIds((prev) => prev.filter((id) => id !== elId));
      }
      if (elIsCasinoEl) {
        setCasinoAssetElIds((prev) => prev.filter((id) => id !== elId));
      }
    }

    if (newType === 'casino') {
      if (!elIsCasinoEl) {
        setCasinoAssetElIds((prev) => [...prev, elId]);
      }
      if (elIsTeamAssetEl) {
        setTeamAssetElIds((prev) => prev.filter((id) => id !== elId));
      }
    }
  };

  const onToggleMapping = useCallback(
    (selected: boolean) => {
      setCheckedElements(
        checkedElements.map((el) => {
          const match = Datasources.pickSuggestionGroupForElementImport(
            filteredSuggestions,
            el.id,
            dataSourceTypes,
          );
          if (match) {
            return { ...el, selected };
          }
          return el;
        }),
      );
      toggleDataMapping();
    },
    [checkedElements, toggleDataMapping, filteredSuggestions, dataSourceTypes],
  );

  const getImageElementType = useCallback(
    (elId: string) => {
      if (!mappingEnabled) {
        return;
      }
      const match = Datasources.pickSuggestionGroupForElementImport(
        filteredSuggestions,
        elId,
        dataSourceTypes,
      );
      if (!match) {
        return;
      }
      if (teamAssetElIds.includes(elId)) return 'teamasset';
      if (casinoAssetElIds.includes(elId)) return 'casino';
      return 'img';
    },
    [filteredSuggestions, mappingEnabled, teamAssetElIds, casinoAssetElIds, dataSourceTypes],
  );

  return (
    <>
      <ModalBody display="flex" alignItems="flex-start" columnGap={5}>
        <Box>
          {state.previewURL && (
            <Image
              data-test-id="loaded-image"
              w={300}
              src={state.previewURL}
              fallback={<Skeleton height={300} width={300} />}
            ></Image>
          )}
          {!state.savedComponentId && (
            <Checkbox mt={3} key="toggleSave" onChange={toggleSaveComponent}>
              <Box display="flex" alignItems="center">
                <FaBookmark /> <Text ml={1}>Save this component for later</Text>
              </Box>
            </Checkbox>
          )}
        </Box>
        <Box flexDirection={'column'}>
          <Box
            borderWidth={1}
            borderRadius="md"
            px={4}
            py={3}
            flex={1}
            data-test-id="select-element"
          >
            <Heading as="h3" size="md">
              Element Selection
            </Heading>
            <Text size="md" mb={3}>
              Select any additional elements you would like to create.
            </Text>
            {state.warnings.length > 0 && (
              <Alert status="warning" alignItems="flex-start" mb={3} data-test-id="import-warnings">
                <AlertIcon />
                <Stack spacing={1}>
                  {state.warnings.map((warning, index) => (
                    <Text key={index} fontSize="sm">
                      {warning}
                    </Text>
                  ))}
                </Stack>
              </Alert>
            )}
            <Stack
              direction="column"
              maxH={'300px'}
              overflowY={'scroll'}
              data-test-id="elements-list"
            >
              {checkedElements.map((el, index) => {
                if (el.type === 'SVG') {
                  return (
                    <Checkbox pl={1} key={el.id} defaultChecked isDisabled>
                      <Box display={'flex'} alignItems="center">
                        <FaShapes /> <Text ml={2}>{el.name}</Text>
                      </Box>
                    </Checkbox>
                  );
                }
                if (el.type === 'WIDGET') {
                  if (!widgetLayersEnabled) return null;
                  // Always imported, so the placeholder is always removed from the background.
                  return (
                    <Checkbox pl={1} key={el.id} isChecked isDisabled>
                      <Box display={'flex'} alignItems="center">
                        <Icon as={LuAppWindow} aria-label="Widget" />
                        <Text ml={2}>{el.name}</Text>
                      </Box>
                    </Checkbox>
                  );
                }
                if (el.type === 'TEXT') {
                  return (
                    <Checkbox
                      pl={1}
                      key={el.id}
                      isChecked={el.selected}
                      onChange={onChangeHandle(index)}
                    >
                      <Box display={'flex'} alignItems="center">
                        <Icon as={LuType}></Icon>
                        <Text ml={2} noOfLines={1} as="span">
                          {el.name}
                          <Text ml={2} fontStyle="italic" fontSize="xs" as="span">
                            - &quot;{el.data.content}&quot;
                          </Text>
                        </Text>
                      </Box>
                    </Checkbox>
                  );
                }
                if (el.type === 'IMAGE') {
                  return (
                    <Flex key={el.id} justifyContent="space-between">
                      <Checkbox pl={1} isChecked={el.selected} onChange={onChangeHandle(index)}>
                        <Box display={'flex'} alignItems="center">
                          {teamAssetElIds.includes(el.id) ? (
                            <GiAmericanFootballHelmet />
                          ) : casinoAssetElIds.includes(el.id) ? (
                            <CasinoIcon />
                          ) : (
                            <LuImage />
                          )}
                          <Text ml={2}>{el.name}</Text>
                        </Box>
                      </Checkbox>
                      <ImgElTypeRadioGroup
                        onChange={(type) => handleToggleImgElType(el.id, type)}
                        name={`${el.id}_assettype-toggle`}
                        val={getImageElementType(el.id)}
                        casinoTemplatesEnabled={casinoTemplatesEnabled}
                      />
                    </Flex>
                  );
                }
                if (el.type === 'VECTOR') {
                  return (
                    <Checkbox
                      pl={1}
                      key={el.id}
                      isChecked={el.selected}
                      onChange={onChangeHandle(index)}
                    >
                      <Box display={'flex'} alignItems="center">
                        <PiBoundingBox />
                        <Text ml={2}>{el.name}</Text>
                      </Box>
                    </Checkbox>
                  );
                }
                return null;
              })}
            </Stack>
          </Box>
          <Box
            borderWidth={1}
            borderRadius="md"
            mt={2}
            px={4}
            py={3}
            flex={1}
            data-test-id="select-element"
          >
            <DataMappingMagic
              mappingEnabled={mappingEnabled}
              onToggleMapping={onToggleMapping}
              suggestedDataSources={filteredSuggestions}
              inferredImportConfig={resolvedImportConfig}
              sourceFamilyOptions={sourceFamilyOptions}
              selectedSourceFamily={selectedSourceFamily}
              competitionOptions={competitionOptions}
              selectedCompetition={selectedCompetition}
              selectedStage={selectedStage}
              onImportOverridesChange={setImportOverrides}
            />
          </Box>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button data-test-id="close-button" variant="ghost" mr={3} onClick={onClose}>
          Close
        </Button>
        <Button colorScheme="blue" onClick={handleSubmit} data-test-id="finish-selection">
          Finish
        </Button>
      </ModalFooter>
    </>
  );
};

ComponentImportStepThree.displayName = 'ComponentImportStepThree';
export default ComponentImportStepThree;
